import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { Play, Copy, Download, Send, Sparkles, Trash2, Check, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import APIService from '../services/apiService';
import './MainEditor.css';

interface MainEditorProps {
  language: string;
  sidebarCollapsed: boolean;
  code: string;
  setCode: (code: string) => void;
  settings: any;
  isMobile?: boolean;
}

const languageMap: { [key: string]: { id: number; editor: string } } = {
  javascript: { id: 93, editor: 'javascript' },
  typescript: { id: 74, editor: 'typescript' },
  python: { id: 71, editor: 'python' },
  java: { id: 62, editor: 'java' },
  cpp: { id: 54, editor: 'cpp' },
  go: { id: 60, editor: 'go' },
  rust: { id: 73, editor: 'rust' },
  php: { id: 68, editor: 'php' },
};


const MainEditor: React.FC<MainEditorProps> = ({ language, sidebarCollapsed, code, setCode, settings, isMobile = false }) => {
  // Using code and setCode from props instead of local state
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState<{ text: string, type: 'stdout' | 'stderr' | 'system' }[]>([]);
  const [stdin, setStdin] = useState('');
  const outputRef = useRef<HTMLDivElement>(null);
  const [deletedCode, setDeletedCode] = useState<string | null>(null);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [ioHeight, setIoHeight] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [simplifiedError, setSimplifiedError] = useState<string | null>(null);
  // Default to false so IO stays in its normal position; mobile users can toggle it
  const [showIOAbove, setShowIOAbove] = useState<boolean>(false);

  useEffect(() => {
    let timer: any;
    if (showUndoToast && undoCountdown > 0) {
      timer = setInterval(() => {
        setUndoCountdown((prev: number) => prev - 1);
      }, 1000);
    } else if (undoCountdown === 0) {
      setShowUndoToast(false);
      setDeletedCode(null);
    }
    return () => clearInterval(timer);
  }, [showUndoToast, undoCountdown]);

  const formatOutputText = (text: string) => {
    if (!text) return text;
    // Simple Error Cleaner: detect common tracebacks and simplify
    if (text.includes('Traceback') || text.includes('Error:') || text.includes('Exception')) {
      const lines = text.split('\n');
      const errorLine = lines.find(l => l.includes('Error:') || l.includes('Exception')) || lines[lines.length - 1];
      return `❌ CLEAN ERROR: ${errorLine.trim()}`;
    }
    return text;
  };

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const handleEditorChange = (value: string | undefined) => {
    setCode(value || '');
  };

  const handleRunCode = async () => {
    if (!code.trim()) return;
    setIsRunning(true);
    setOutput([]);

    let processedStdin = stdin;
    if (language === 'python') {
      processedStdin = stdin.replace(/,/g, '.');
    }

    const options = {
      method: 'POST',
      url: 'https://judge0-ce.p.rapidapi.com/submissions',
      params: {
        base64_encoded: 'true',
        fields: ''
      },
      headers: {
        'content-type': 'application/json',
        'Content-Type': 'application/json',
        'X-RapidAPI-Key': '6ffa919effmshd6ed0125ba64926p1106bajsne21b57b2e322',
        'X-RapidAPI-Host': 'judge0-ce.p.rapidapi.com'
      },
      data: {
        language_id: languageMap[language]?.id,
        source_code: btoa(code),
        stdin: btoa(processedStdin),
      }
    };

    try {
      const response = await axios.request(options);
      const token = response.data.token;
      setSimplifiedError(null);
      checkStatus(token);
    } catch (error: any) {
      setOutput(prev => [...prev, { text: error.message, type: 'system' }]);
      setIsRunning(false);
    }
  };

  const handleResize = (e: MouseEvent) => {
    if (!isResizing) return;
    const newHeight = window.innerHeight - e.clientY;
    if (newHeight > 100 && newHeight < window.innerHeight - 200) {
      setIoHeight(newHeight);
    }
  };

  const stopResizing = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', stopResizing);
    } else {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  const checkStatus = async (token: string) => {
    const options = {
      method: 'GET',
      url: `https://judge0-ce.p.rapidapi.com/submissions/${token}`,
      params: {
        base64_encoded: 'true',
        fields: ''
      },
      headers: {
        'X-RapidAPI-Key': '6ffa919effmshd6ed0125ba64926p1106bajsne21b57b2e322',
        'X-RapidAPI-Host': 'judge0-ce.p.rapidapi.com'
      }
    };

    try {
      const response = await axios.request(options);
      const statusId = response.data.status.id;

      if (statusId === 1 || statusId === 2) { // In Queue or Processing
        setTimeout(() => {
          checkStatus(token);
        }, 2000);
        return;
      }

      setIsRunning(false);

      const stats = `Time: ${response.data.time || 0}s | Memory: ${response.data.memory || 0}KB | Status: ${response.data.status?.description || 'Finished'}`;
      setOutput((prev: any[]) => [...prev, { text: stats, type: 'system' }]);

      if (response.data.stdout) {
        setOutput((prev: any[]) => [...prev, { text: atob(response.data.stdout), type: 'stdout' }]);
      } else if (response.data.stderr || response.data.compile_output) {
        const rawError = atob(response.data.stderr || response.data.compile_output);
        setOutput((prev: any[]) => [...prev, { text: formatOutputText(rawError), type: 'stderr' }]);

        // Simplify error using AI
        if (!settings.schoolMode) {
          try {
            const simple = await APIService.simplifyError(rawError, language);
            setSimplifiedError(simple);
          } catch (e) {
            console.error('Failed to simplify error', e);
          }
        }
      } else {
        setOutput((prev: any[]) => [...prev, { text: 'Execution finished.', type: 'system' }]);
      }
    } catch (error: any) {
      setOutput((prev: any[]) => [...prev, { text: error.message, type: 'system' }]);
      setIsRunning(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || settings.schoolMode) return;

    setIsGenerating(true);

    try {
      const result = await APIService.generateCode({
        prompt,
        language,
        context: code
      });

      if (result.code) {
        const generatedCode = `# Generated for: "${prompt}"\n${result.code}`;
        setCode(generatedCode + '\n\n' + code);

        if (result.explanation) {
          setOutput((prev: any[]) => [...prev, {
            text: result.explanation || "",
            type: 'system'
          }]);
        }
      }
    } catch (error: any) {
      console.error('Error generating code:', error);
      setOutput((prev: any[]) => [...prev, { text: `AI Error: ${error.message}`, type: 'system' }]);
    } finally {
      setIsGenerating(false);
      setPrompt('');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `main.${languageMap[language]?.editor || 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearAll = () => {
    setDeletedCode(code);
    setCode('');
    setUndoCountdown(5);
    setShowUndoToast(true);
  };

  const handleUndo = () => {
    if (deletedCode !== null) {
      setCode(deletedCode);
      setDeletedCode(null);
      setShowUndoToast(false);
      setUndoCountdown(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleGenerate();
    }
  };

  return (
    <div className={`main-editor ${sidebarCollapsed ? 'sidebar-collapsed' : ''} animate-fade-in`}>
      <div className="editor-header">
        <div className="file-tabs">
          <div className="file-tab active">
            <span>main.{languageMap[language]?.editor || 'js'}</span>
          </div>
        </div>
        <div className="editor-actions">
          {isMobile && (
            <button className="action-btn" title={showIOAbove ? 'Move IO below editor' : 'Move IO above editor'} onClick={() => setShowIOAbove(prev => !prev)}>
              {showIOAbove ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          )}
          <button className="action-btn" title="Clear All" onClick={handleClearAll}>
            <Trash2 size={16} />
          </button>
          <button className="action-btn" title="Copy Code" onClick={handleCopy}>
            {copyFeedback ? <Check size={16} color="#4ade80" /> : <Copy size={16} />}
          </button>
          <button className="action-btn" title="Download Code" onClick={handleDownload}>
            <Download size={16} />
          </button>
          <button className="action-btn" onClick={handleRunCode} disabled={isRunning} title="Run Code">
            {isRunning ? <div className="spinner"></div> : <Play size={16} />}
          </button>
        </div>
      </div>

      <div className="prompt-section">
        <div className="prompt-container animate-slide-up">
          <Sparkles className="prompt-icon" size={20} />
          <input
            type="text"
            placeholder="Describe what code you want to generate..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            className="prompt-input"
            disabled={isGenerating || settings.schoolMode}
          />
          <button
            className={`generate-btn ${isGenerating ? 'generating' : ''}`}
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating || settings.schoolMode}
          >
            {isGenerating ? (
              <div className="spinner"></div>
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>

      {/* On mobile we allow toggling IO placement above the editor */}
      {isMobile && (output.length > 0 || isRunning) && showIOAbove && (
        <>
          <div className="resizer" onMouseDown={() => setIsResizing(true)}></div>
          <div className="io-container" style={{ height: ioHeight }}>
            <div className="stdin-container">
              <div className="stdin-header">
                <h4>Input</h4>
              </div>
              <textarea
                className="stdin-content"
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
                placeholder="Enter input here..."
              />
            </div>
            <div className="output-container">
              <div className="output-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="term-tabs">
                    <div className="term-tab active">Terminal</div>
                  </div>
                  <h4>Output</h4>
                </div>
                <div>
                  <button className="clear-btn" onClick={() => { setOutput([]); setSimplifiedError(null); }}>Clear Terminal</button>
                </div>
              </div>
              <div ref={outputRef} className="output-terminal animate-fade-in">
                {!simplifiedError && output.length === 0 && !isRunning && (
                  <div className="term-line system" style={{ opacity: 0.5, textAlign: 'center', marginTop: '20px' }}>
                    <p>Terminal ready. Waiting for input or code execution...</p>
                  </div>
                )}
                {simplifiedError && (
                  <div className="term-line simplified-error">
                    <Sparkles size={14} style={{ color: '#a855f7' }} />
                    <span>{simplifiedError}</span>
                  </div>
                )}
                {isRunning && <div className="term-line system">• Running subprocess...</div>}
                {output.map((o: any, idx: number) => (
                  <div key={idx} className={`term-line ${o.type}`}>
                    <span className="term-content">{o.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="editor-container" style={{ height: (output.length > 0 || isRunning) ? `calc(100vh - ${ioHeight + 180}px)` : "calc(100vh - 180px)" }}>
        <Editor
          height="100%"
          language={languageMap[language]?.editor || 'javascript'}
          value={code}
          onChange={handleEditorChange}
          onMount={(editor) => {
            editor.onKeyDown((e) => {
              if (language === 'python' && e.keyCode === 3 /* Enter */) {
                const model = editor.getModel();
                const position = editor.getPosition();
                if (model && position) {
                  const lineContent = model.getLineContent(position.lineNumber).trim();
                  if (lineContent.startsWith('#') && lineContent.length > 2) {
                    e.preventDefault();
                    e.stopPropagation();
                    const aiPrompt = lineContent.substring(1).trim();
                    setPrompt(aiPrompt);
                    // We need a way to trigger generate. Since setPrompt is async, 
                    // we'll use a small trick or just call a modified generate.
                    setTimeout(() => {
                      const btn = document.querySelector('.generate-btn') as HTMLButtonElement;
                      if (btn) btn.click();
                    }, 100);
                  }
                }
              }
            });
          }}
          theme="vs-dark"
          options={{
            minimap: { enabled: true },
            fontSize: settings.fontSize || 14,
            lineNumbers: 'on',
            roundedSelection: false,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            wordWrap: 'on',
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            parameterHints: { enabled: true },
          }}
        />
      </div>

      {showUndoToast && (
        <div className="undo-toast">
          <div className="undo-content">
            <Trash2 size={16} />
            <span>Workspace cleared ({undoCountdown}s)</span>
          </div>
          <button className="undo-btn" onClick={handleUndo}>
            <RotateCcw size={14} />
            Undo
          </button>
        </div>
      )}
      {(output.length > 0 || isRunning) && (!isMobile || !showIOAbove) && (
        <>
          <div className="resizer" onMouseDown={() => setIsResizing(true)}></div>
          <div className="io-container" style={{ height: ioHeight }}>
            <div className="stdin-container">
              <div className="stdin-header">
                <h4>Input</h4>
              </div>
              <textarea
                className="stdin-content"
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
                placeholder="Enter input here..."
              />
            </div>
            <div className="output-container">
              <div className="output-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="term-tabs">
                    <div className="term-tab active">Terminal</div>
                  </div>
                  <h4>Output</h4>
                </div>
                <div>
                  <button className="clear-btn" onClick={() => { setOutput([]); setSimplifiedError(null); }}>Clear Terminal</button>
                </div>
              </div>
              <div ref={outputRef} className="output-terminal animate-fade-in">
                {!simplifiedError && output.length === 0 && !isRunning && (
                  <div className="term-line system" style={{ opacity: 0.5, textAlign: 'center', marginTop: '20px' }}>
                    <p>Terminal ready. Waiting for input or code execution...</p>
                  </div>
                )}
                {simplifiedError && (
                  <div className="term-line simplified-error">
                    <Sparkles size={14} style={{ color: '#a855f7' }} />
                    <span>{simplifiedError}</span>
                  </div>
                )}
                {isRunning && <div className="term-line system">• Running subprocess...</div>}
                {output.map((o: any, idx: number) => (
                  <div key={idx} className={`term-line ${o.type}`}>
                    <span className="term-content">{o.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MainEditor;
